import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "components/snackbar";
import SafeArea from "components/safe-area";
import FlatContainer from "components/container/flat";
import Table from "components/table";
import { Button, SubtleButton } from "components/button";
import { Status } from "components/status";
import { IEmailTemplate } from "./types/IEmailTemplate";
import { getTemplates, deleteTemplate, cloneTemplate } from "./api/templates";

const TemplateList = () => {
  const { t } = useTranslation();
  const tBase = "views.outreach.templates.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const history = useHistory();
  const queryClient = useQueryClient();
  const snackBar = useSnackbarContext();

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const {
    data: templates,
    isLoading,
    isError,
  } = useQuery<IEmailTemplate[]>("email-templates", getTemplates);

  const deleteMutation = useMutation(deleteTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("email-templates");
      snackBar.showSnackbar(tr("deleteSuccess"));
      setDeleteConfirmId(null);
    },
    onError: () => {
      snackBar.showSnackbar(tr("deleteError"));
      setDeleteConfirmId(null);
    },
  });

  const cloneMutation = useMutation(cloneTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("email-templates");
      snackBar.showSnackbar(tr("cloneSuccess"));
    },
    onError: () => {
      snackBar.showSnackbar(tr("cloneError"));
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleClone = (id: string) => {
    cloneMutation.mutate(id);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const columns = {
    name: {
      label: tr("colName"),
      style: { fontWeight: 600 },
      cell: (row: IEmailTemplate) => row.name,
    },
    content: {
      label: tr("colContent"),
      cell: (row: IEmailTemplate) => {
        const type = (row as any).templateType || "email";
        return type === "sms"
          ? (row as any).messageText
            ? String((row as any).messageText).substring(0, 60) + ((row as any).messageText?.length > 60 ? "..." : "")
            : ""
          : row.subject;
      },
    },
    channelType: {
      label: tr("colChannel"),
      cell: (row: IEmailTemplate) => {
        const type = (row as any).templateType || "email";
        return (
          <Status color={type === "sms" ? "34,197,94" : "59,130,246"}>
            {type === "sms" ? tr("channelSms") : tr("channelEmail")}
          </Status>
        );
      },
    },
    type: {
      label: tr("colType"),
      cell: (row: IEmailTemplate) => (
        <Status color={row.isSystemTemplate ? "147,51,234" : "0,200,100"}>
          {row.isSystemTemplate ? tr("typeSystem") : tr("typeCustom")}
        </Status>
      ),
    },
    updatedAt: {
      label: tr("colLastUpdated"),
      cell: (row: IEmailTemplate) => formatDate(row.updatedAt),
    },
    actions: {
      label: tr("colActions"),
      disableSort: true,
      disableRowClick: true,
      cell: (row: IEmailTemplate) => {
        if (row.isSystemTemplate) {
          return (
            <SubtleButton
              primary
              size="small"
              onClick={() => handleClone(row.id)}
              disabled={cloneMutation.isLoading}
            >
              {tr("clone")}
            </SubtleButton>
          );
        }
        return (
          <div className="flex items-center space-x-2">
            <SubtleButton
              primary
              size="small"
              onClick={() =>
                history.push(`/outreach/templates/${row.id}/edit`)
              }
            >
              {tr("edit")}
            </SubtleButton>
            {deleteConfirmId === row.id ? (
              <span className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {tr("confirmDelete")}
                </span>
                <SubtleButton
                  $warning
                  size="small"
                  onClick={() => handleDelete(row.id)}
                  disabled={deleteMutation.isLoading}
                >
                  {tr("yes")}
                </SubtleButton>
                <SubtleButton
                  size="small"
                  onClick={() => setDeleteConfirmId(null)}
                >
                  {tr("no")}
                </SubtleButton>
              </span>
            ) : (
              <SubtleButton
                $warning
                size="small"
                onClick={() => setDeleteConfirmId(row.id)}
              >
                {tr("delete")}
              </SubtleButton>
            )}
          </div>
        );
      },
    },
  };

  const tableData = isLoading ? undefined : isError ? [] : templates || [];

  return (
    <SafeArea>
      <FlatContainer
        title={<h2>{tr("title")}</h2>}
        buttons={[
          <Button
            primary
            key="new"
            onClick={() => history.push("/outreach/templates/new")}
          >
            + {tr("newTemplate")}
          </Button>,
        ]}
      >
        {isError && (
          <p className="text-red-500 py-4">{tr("loadError")}</p>
        )}
        <Table
          data={tableData}
          columns={columns}
          disableSortAll
          handleClick={(row: IEmailTemplate) => {
            history.push(`/outreach/templates/${row.id}/edit`);
          }}
        />
      </FlatContainer>
    </SafeArea>
  );
};

export default TemplateList;
